import React, { forwardRef, memo, Ref, useEffect, useRef, useState } from 'react';
import Map from '@/components/Map';
import Div from '@/shared/Div';
import Paragraph from '@/shared/Paragraph';
import ChevronUpIcon from '@/components/Icons/ChevronUpIcon';
import Checked from '@/components/Icons/Checked';
import ErrorIcon from '@/components/Icons/ErrorIcon';
import { FieldError, FieldErrors } from 'react-hook-form';
import VALIDATION_ERRORS from '@/constants/validationErrors';
import classNames from 'classnames';

type DropdownProps = {
  dropdownList: DropdownItem[];
  onChange: (value: DropdownItem) => void;
  errors?: FieldErrors;
  name: string;
  selectedValue?: any;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
};
type DropdownItem = {
  id: string;
  label: string;
};

const Dropdown = (
  {
    dropdownList,
    onChange,
    errors,
    name,
    selectedValue: propSelectedValue,
    placeholder,
    id,
    disabled,
  }: DropdownProps,
  ref: Ref<HTMLSelectElement>,
) => {
  const [selectedValue, setSelectedValue] = useState<DropdownItem>(
    propSelectedValue || dropdownList[0],
  );

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fieldError: FieldError | undefined = (errors?.[name] as FieldError) ?? undefined;
  const errorMessage = fieldError?.message || VALIDATION_ERRORS[fieldError?.type];

  const handleChange = (value: DropdownItem) => {
    setSelectedValue(value);
    setOpen(false);
    onChange(value);
  };

  useEffect(() => {
    if (propSelectedValue && propSelectedValue?.id !== selectedValue?.id) {
      setSelectedValue(propSelectedValue);
    }
  }, [propSelectedValue]);

  const handleBlur = (e: React.FocusEvent) => {
    if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
      setOpen(false);
    }
  };

  const toggle = () => {
    if (disabled) return;
    setOpen(!open);
  };

  return (
    <Div>
      <select
        ref={ref}
        id={id}
        className="h-0 w-0 hidden"
        value={selectedValue?.id}
        disabled={disabled}
        name={name}
        onChange={(e) => handleChange(dropdownList.find((item) => item?.id === e.target.value)!)}
      >
        <Map
          list={dropdownList}
          render={({ id, label }) => {
            return (
              <option key={id} value={id}>
                {label}
              </option>
            );
          }}
        ></Map>
      </select>
      <div
        ref={dropdownRef}
        tabIndex={-1}
        onBlur={handleBlur}
        className={classNames('relative rounded-cxs')}
      >
        <Div
          className={classNames(
            'border focus:outline-blue-300 p-s4 w-full cursor-pointer flex rounded-cxs border-cs-disabled',
            {
              'rounded-t-cxs shadow-subtle': open,
              'border-cu-negative ': errorMessage,
              '!cursor-not-allowed': disabled,
            },
          )}
          onClick={toggle}
        >
          <Paragraph
            className={classNames('flex-1 text-body-lg text-cm-primary', {
              '!text-black/30': !(selectedValue?.id && selectedValue.label) && placeholder,
            })}
          >
            {selectedValue?.label || placeholder}
          </Paragraph>
          {errorMessage ? (
            <ErrorIcon />
          ) : (
            <Div className={`ransition-all ${open ? '' : 'rotate-180 t'}`}>
              <ChevronUpIcon fill={'#171717'} />
            </Div>
          )}
        </Div>
        {open ? null : <Paragraph className="pt-s3 text-cu-negative">{errorMessage}</Paragraph>}
        {open ? (
          <ul
            className={classNames(
              'absolute top-0 z-40 bg-white border border-neutral-100 max-h-48 overflow-auto w-full rounded-cxs',
              {
                '!rounded-cs': !open,
              },
            )}
          >
            <Map
              list={dropdownList}
              render={({ id, label }) => (
                <li
                  key={id}
                  className={classNames('cursor-pointer px-s5 py-s4 flex', {
                    'bg-cs-secondary': id === selectedValue?.id,
                  })}
                  onClick={(e) => handleChange({ id, label })}
                  value={id}
                >
                  <Paragraph className="flex-1 text-body-lg text-cm-primary">{label}</Paragraph>
                  {id === selectedValue.id ? (
                    <Checked fill={'#171717'} width={'20px'} height={'20px'} />
                  ) : null}
                </li>
              )}
            />
          </ul>
        ) : null}
      </div>
    </Div>
  );
};
export default memo(forwardRef(Dropdown));
